import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  MapPin,
  Phone,
  Calendar,
  Building2,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Link2,
  Edit2,
  Mail,
  Layout,
} from "lucide-react";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../lib/usePerformRequest";
import { DashboardEndpoints } from "../../../lib/endpoints-dashboard";
import { IOrganizationProps } from "../../../types/Organization";
import { Loader } from "../../../components/ui/loader";
import { EmptyState } from "../../../components/ui/empty-state";
import {
  Button,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Divider,
  Badge,
  Modal,
  TextInput,
} from "@mantine/core";
import "./SingleOrganization.scss";
import { IconPaletteFilled } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import ThemeSelectionModal from "../../../components/ThemeSelectionModal";
import { useToastNotification } from "../../../hooks/useToast";
import { HttpCodes } from "../../../lib/data";

const SingleOrganization: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const organizationId = id ? parseInt(id, 10) : null;
  const toast = useToastNotification();
  const [
    isSelectThemeModalOpen,
    { open: OpenSelectThemeModal, close: CloseSelectThemeModal },
  ] = useDisclosure(false);
  const [
    isCustomDomainModalOpen,
    { open: OpenCustomDomainModal, close: CloseCustomDomainModal },
  ] = useDisclosure(false);
  const [isSlugModalOpen, { open: OpenSlugModal, close: CloseSlugModal }] =
    useDisclosure(false);
  const [customDomain, setCustomDomain] = useState("");
  const [slug, setSlug] = useState("");
  const [isSubmittingDomain, setIsSubmittingDomain] = useState(false);
  const [isUpdatingSlug, setIsUpdatingSlug] = useState(false);
  const organizationEndpoint = React.useMemo(() => {
    if (!organizationId) return null;
    return DashboardEndpoints.GetSingleOrganization(organizationId);
  }, [organizationId]);

  const {
    data: orgResponse,
    isLoading,
    reloadData: reloadOrganizationData,
  } = usePerformRequest<IOrganizationProps>({
    method: organizationEndpoint?.method as any,
    route: organizationEndpoint?.route || "",
  });

  const organization = React.useMemo(() => {
    if (!orgResponse) return null;
    const data = (orgResponse as any)?.data || orgResponse;
    return data as IOrganizationProps;
  }, [orgResponse]);

  // Set initial custom domain value when organization loads or modal opens
  React.useEffect(() => {
    if (organization && isCustomDomainModalOpen) {
      // Check if organization has a website field that might contain the custom domain
      const orgAny = organization as any;
      setCustomDomain(orgAny.custom_domain || orgAny.website || "");
    }
  }, [organization, isCustomDomainModalOpen]);

  // Set initial slug value when organization loads or modal opens
  React.useEffect(() => {
    if (organization && isSlugModalOpen) {
      setSlug(organization.slug || "");
    }
  }, [organization, isSlugModalOpen]);

  const handleSetCustomDomain = async () => {
    if (!organizationId) {
      toast.error("Organization ID is missing");
      return;
    }

    if (!customDomain.trim()) {
      toast.error("Please enter a custom domain");
      return;
    }

    setIsSubmittingDomain(true);
    try {
      // Create FormData as shown in Postman
      const formData = new FormData();
      formData.append("organization_id", organizationId.toString());
      formData.append("website", customDomain.trim());

      const endpoint = DashboardEndpoints.GetSetOrganizationWebsite();
      const response = await PerformRequest<any>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
        onError: () => {
          setIsSubmittingDomain(false);
        },
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        toast.success("Custom domain updated successfully!");
        CloseCustomDomainModal();
        // Reload organization data to get updated website
        if (reloadOrganizationData) {
          await reloadOrganizationData();
        }
      } else {
        toast.error(
          response.message ||
            "Failed to update custom domain. Please try again."
        );
      }
    } catch (error) {
      console.error("Error setting custom domain:", error);
      toast.error("An error occurred while updating the custom domain");
    } finally {
      setIsSubmittingDomain(false);
    }
  };

  const handleUpdateSlug = async () => {
    if (!organizationId) {
      toast.error("Organization ID is missing");
      return;
    }

    if (!slug.trim()) {
      toast.error("Please enter a slug");
      return;
    }

    // Validate slug format (lowercase, alphanumeric, hyphens only)
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(slug.trim())) {
      toast.error(
        "Slug must contain only lowercase letters, numbers, and hyphens. It cannot start or end with a hyphen."
      );
      return;
    }

    setIsUpdatingSlug(true);
    try {
      const formData = new FormData();
      formData.append("slug", slug.trim());

      const endpoint =
        DashboardEndpoints.GetUpdateOrganizationSlug(organizationId);
      const response = await PerformRequest<any>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
        onError: () => {
          setIsUpdatingSlug(false);
        },
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        toast.success("Organization slug updated successfully!");
        CloseSlugModal();
        // Reload organization data to get updated slug
        if (reloadOrganizationData) {
          await reloadOrganizationData();
        }
      } else {
        toast.error(
          response.message || "Failed to update slug. Please try again."
        );
      }
    } catch (error) {
      console.error("Error updating slug:", error);
      toast.error("An error occurred while updating the slug");
    } finally {
      setIsUpdatingSlug(false);
    }
  };

  if (isLoading) {
    return (
      <div className="single-organization-page">
        <div className="single-organization-loading">
          <Loader size="lg" />
          <Text c="dimmed" mt="md">
            Loading organization details...
          </Text>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="single-organization-page">
        <EmptyState
          title="Organization not found"
          description="The organization you're looking for doesn't exist or has been removed."
          action={
            <Button
              onClick={() => navigate("/d/organizations")}
              style={{ backgroundColor: "#17265a" }}
            >
              Back to Organizations
            </Button>
          }
        />
      </div>
    );
  }

  const socialLinks = [
    { icon: Facebook, url: organization.facebook_link, label: "Facebook" },
    { icon: Instagram, url: organization.instagram_link, label: "Instagram" },
    { icon: Linkedin, url: organization.linkedin_link, label: "LinkedIn" },
    { icon: Twitter, url: organization.x_link, label: "Twitter" },
    { icon: Youtube, url: organization.youtube_link, label: "YouTube" },
  ].filter((link) => link.url);

  return (
    <div className="single-organization-page">
      <div className="single-organization-container">
        {/* Header Section */}
        <div className="single-organization-header">
          <Button
            variant="subtle"
            leftSection={<ArrowLeft size={18} />}
            onClick={() => navigate("/d/organizations")}
            className="back-button"
          >
            Back to Organizations
          </Button>
          <Group gap="sm">
            <Button
              leftSection={<Layout size={18} />}
              color="#17265a"
              size="md"
              onClick={() =>
                navigate(`/d/organizations/${id}/craft-page-builder`)
              }
            >
              Customize Page
            </Button>
            <Button
              leftSection={<Layout size={18} />}
              color="#17265a"
              variant="outline"
              size="md"
              onClick={() =>
                navigate(`/d/organizations/${id}/grapes-page-builder`)
              }
            >
              GrapesJS
            </Button>
            <Button
              leftSection={<IconPaletteFilled size={18} />}
              color="#17265a"
              size="md"
              onClick={() => OpenSelectThemeModal()}
            >
              Select Theme
            </Button>
          </Group>
        </div>

        {/* Hero Section */}
        <div className="single-organization-hero">
          <div className="organization-logo-section">
            {organization.logo ? (
              <img
                src={organization.logo}
                alt={organization.name}
                className="organization-logo"
              />
            ) : (
              <div className="organization-logo-placeholder">
                <Building2 size={48} />
              </div>
            )}
          </div>
          <div className="organization-hero-content">
            <Title order={1} className="organization-name">
              {organization.name}
            </Title>
            {organization.subtitle && (
              <Text
                size="lg"
                c="dimmed"
                mt="xs"
                className="organization-subtitle"
              >
                {organization.subtitle}
              </Text>
            )}
            {organization.description && (
              <Text mt="md" className="organization-description">
                {organization.description}
              </Text>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="organization-stats">
          {organization.number_of_locations > 0 && (
            <Paper className="stat-card" p="md" radius="md" withBorder>
              <MapPin size={24} className="stat-icon" />
              <Text size="xl" fw={700} className="stat-value">
                {organization.number_of_locations}
              </Text>
              <Text size="sm" c="dimmed" className="stat-label">
                {organization.number_of_locations === 1
                  ? "Location"
                  : "Locations"}
              </Text>
            </Paper>
          )}
          {organization.years_of_experience > 0 && (
            <Paper className="stat-card" p="md" radius="md" withBorder>
              <Calendar size={24} className="stat-icon" />
              <Text size="xl" fw={700} className="stat-value">
                {organization.years_of_experience}+
              </Text>
              <Text size="sm" c="dimmed" className="stat-label">
                Years of Experience
              </Text>
            </Paper>
          )}
          <Paper className="stat-card" p="md" radius="md" withBorder>
            <Calendar size={24} className="stat-icon" />
            <Text size="xl" fw={700} className="stat-value">
              {new Date(organization.created_at).getFullYear()}
            </Text>
            <Text size="sm" c="dimmed" className="stat-label">
              Established
            </Text>
          </Paper>
        </div>

        {/* Main Content Grid */}
        <div className="organization-content-grid">
          {/* Left Column */}
          <div className="organization-main-content">
            {/* About Section */}
            {organization.about && (
              <Paper className="content-section" p="xl" radius="md" withBorder>
                <Title order={2} mb="lg">
                  About
                </Title>
                <Stack gap="lg">
                  {organization.about.mission && (
                    <div>
                      <Text
                        fw={600}
                        size="sm"
                        c="dimmed"
                        mb="xs"
                        tt="uppercase"
                        lts={1}
                      >
                        Mission
                      </Text>
                      <Text>{organization.about.mission}</Text>
                    </div>
                  )}
                  {organization.about.vision && (
                    <div>
                      <Text
                        fw={600}
                        size="sm"
                        c="dimmed"
                        mb="xs"
                        tt="uppercase"
                        lts={1}
                      >
                        Vision
                      </Text>
                      <Text>{organization.about.vision}</Text>
                    </div>
                  )}
                  {organization.about.values && (
                    <div>
                      <Text
                        fw={600}
                        size="sm"
                        c="dimmed"
                        mb="xs"
                        tt="uppercase"
                        lts={1}
                      >
                        Values
                      </Text>
                      <Text>{organization.about.values}</Text>
                    </div>
                  )}
                  {organization.about.about && (
                    <div>
                      <Text
                        fw={600}
                        size="sm"
                        c="dimmed"
                        mb="xs"
                        tt="uppercase"
                        lts={1}
                      >
                        About Us
                      </Text>
                      <Text>{organization.about.about}</Text>
                    </div>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Location Section */}
            {organization.location && (
              <Paper className="content-section" p="xl" radius="md" withBorder>
                <Title order={2} mb="lg">
                  Location
                </Title>
                <Stack gap="sm">
                  {organization.location.address && (
                    <Group gap="sm">
                      <MapPin size={18} className="info-icon" />
                      <Text>{organization.location.address}</Text>
                    </Group>
                  )}
                  <Group gap="sm">
                    {organization.location.city && (
                      <Badge variant="light" size="lg">
                        {organization.location.city}
                        {organization.location.state &&
                          `, ${organization.location.state}`}
                      </Badge>
                    )}
                    {organization.location.country && (
                      <Badge variant="light" size="lg">
                        {organization.location.country}
                      </Badge>
                    )}
                    {organization.location.postal_code && (
                      <Badge variant="light" size="lg">
                        {organization.location.postal_code}
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Paper>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="organization-sidebar">
            {/* Contact Information */}
            <Paper className="sidebar-section" p="xl" radius="md" withBorder>
              <Title order={3} mb="lg">
                Contact Information
              </Title>
              <Stack gap="md">
                {organization.website && (
                  <Group gap="sm" className="contact-item">
                    <Globe size={18} className="contact-icon" />
                    <a
                      href={organization.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      {organization.website}
                    </a>
                  </Group>
                )}
                {organization.phone_number && (
                  <Group gap="sm" className="contact-item">
                    <Phone size={18} className="contact-icon" />
                    <a
                      href={`tel:${organization.phone_number}`}
                      className="contact-link"
                    >
                      {organization.phone_number}
                    </a>
                  </Group>
                )}
                {organization.email && (
                  <Group gap="sm" className="contact-item">
                    <Mail size={18} className="contact-icon" />
                    <a
                      href={`mailto:${organization.email}`}
                      className="contact-link"
                    >
                      {organization.email}
                    </a>
                  </Group>
                )}
              </Stack>
            </Paper>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <Paper className="sidebar-section" p="xl" radius="md" withBorder>
                <Title order={3} mb="lg">
                  Follow Us
                </Title>
                <Stack gap="sm">
                  {socialLinks.map(({ icon: Icon, url, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      <Icon size={18} />
                      <Text>{label}</Text>
                    </a>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Aiserwave Subdomain Section */}
            <Paper className="sidebar-section" p="xl" radius="md" withBorder>
              <Group justify="space-between" mb="lg">
                <Title order={3}>Aiserwave Subdomain</Title>
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<Edit2 size={16} />}
                  onClick={OpenSlugModal}
                >
                  {organization.slug ? "Edit" : "Set Slug"}
                </Button>
              </Group>
              <Stack gap="sm">
                {organization.slug ? (
                  <>
                    <Group gap="sm" className="contact-item">
                      <Link2 size={18} className="contact-icon" />
                      <a
                        href={`https://${organization.slug}.aiserwave.com`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-link"
                        style={{ wordBreak: "break-all" }}
                      >
                        {organization.slug}.aiserwave.com
                      </a>
                    </Group>
                    <Text size="xs" c="dimmed" mt="xs">
                      Your organization homepage on Aiserwave
                    </Text>
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    Set a slug to create your organization subdomain
                  </Text>
                )}
              </Stack>
            </Paper>

            {/* Custom Domain Section */}
            <Paper className="sidebar-section" p="xl" radius="md" withBorder>
              <Group justify="space-between" mb="lg">
                <Title order={3}>Custom Domain</Title>
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<Edit2 size={16} />}
                  onClick={OpenCustomDomainModal}
                >
                  Configure
                </Button>
              </Group>
              <Stack gap="sm">
                {(organization as any)?.custom_domain ||
                (organization as any)?.website ? (
                  <Group gap="sm" className="contact-item">
                    <Link2 size={18} className="contact-icon" />
                    <Text size="sm">
                      {(organization as any)?.custom_domain ||
                        (organization as any)?.website ||
                        "Not configured"}
                    </Text>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    Connect your homepage to a custom domain
                  </Text>
                )}
              </Stack>
            </Paper>

            {/* Organization Details */}
            <Paper className="sidebar-section" p="xl" radius="md" withBorder>
              <Title order={3} mb="lg">
                Details
              </Title>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Created
                  </Text>
                  <Text size="sm" fw={500}>
                    {new Date(organization.created_at).toLocaleDateString()}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Last Updated
                  </Text>
                  <Text size="sm" fw={500}>
                    {new Date(organization.updated_at).toLocaleDateString()}
                  </Text>
                </Group>
              </Stack>
            </Paper>
          </div>
        </div>
      </div>

      {/* Theme Selection Modal */}
      {organization && (
        <ThemeSelectionModal
          opened={isSelectThemeModalOpen}
          onClose={CloseSelectThemeModal}
          organization={organization}
          onSelectTheme={(themeId, color) => {
            // Handle theme selection here
            // You can add logic to save the selected theme and color
            console.log("Selected theme:", themeId, "with color:", color);
            // TODO: Save theme and color to backend
          }}
        />
      )}

      {/* Custom Domain Modal */}
      <Modal
        opened={isCustomDomainModalOpen}
        onClose={CloseCustomDomainModal}
        title={
          <Group gap="xs">
            <Link2 size={20} />
            <Text fw={700} size="lg">
              Set Custom Domain
            </Text>
          </Group>
        }
        centered
        size="md"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Connect your organization homepage to a custom domain. Enter the
            domain name (e.g., www.example.com or example.com).
          </Text>
          <TextInput
            label="Custom Domain"
            placeholder="www.example.com"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.currentTarget.value)}
            leftSection={<Globe size={18} />}
            description="Enter your custom domain without http:// or https://"
          />
          <Group justify="flex-end" gap="md" mt="md">
            <Button
              variant="subtle"
              onClick={CloseCustomDomainModal}
              disabled={isSubmittingDomain}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetCustomDomain}
              loading={isSubmittingDomain}
              style={{ backgroundColor: "#17265a" }}
            >
              Save Domain
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Slug Update Modal */}
      <Modal
        opened={isSlugModalOpen}
        onClose={CloseSlugModal}
        title={
          <Group gap="xs">
            <Link2 size={20} />
            <Text fw={700} size="lg">
              {organization?.slug ? "Update Slug" : "Set Slug"}
            </Text>
          </Group>
        }
        centered
        size="md"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Your slug is used to create your organization subdomain on
            Aiserwave. Use only lowercase letters, numbers, and hyphens.
          </Text>
          <TextInput
            label="Slug"
            placeholder="organization-slug"
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
            leftSection={<Link2 size={18} />}
            description="This will create: https://{slug}.aiserwave.com"
          />
          {slug && (
            <Text size="xs" c="dimmed">
              Preview:{" "}
              <a
                href={`https://${slug}.aiserwave.com`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#17265a" }}
              >
                https://{slug}.aiserwave.com
              </a>
            </Text>
          )}
          <Group justify="flex-end" gap="md" mt="md">
            <Button
              variant="subtle"
              onClick={CloseSlugModal}
              disabled={isUpdatingSlug}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSlug}
              loading={isUpdatingSlug}
              disabled={!slug.trim() || slug.trim() === organization?.slug}
              style={{ backgroundColor: "#17265a" }}
            >
              {isUpdatingSlug ? "Updating..." : "Save Slug"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default SingleOrganization;
